import { Colors, Icon, Tooltip } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled, { css } from "styled-components";

import {
  DashboardApi,
  DashboardCctDto,
  DashboardCctSearch,
  EBooleanSearchTypes,
  ESearchCctType,
  EStatutContrat
} from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { CircleColumn, CustomBulletList, SearchTablePage, ViewButton } from "../../../components";
import { useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";
import { computeContratStatutColor } from "../../../utils/contratStatutColor";
import { differenceInYears, format, isSameDay } from "date-fns";

const LegendHeaderContainer = styled.div`
  & > * + * {
    margin-left: 0.5rem;
  }
`;

const ColorText = styled.div<{ color: string }>`
  color: ${props => props.color} !important;
  font-weight: bold;
  display: flex;
  align-items: center;
`;

export interface IDashboardCctProps {
}

export const DashboardCct: React.FunctionComponent<IDashboardCctProps> = props => {
  const { t, tUnsafe } = useTl();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();
  const { theme } = useTheme();

  const searchFunction = React.useCallback(
    (sObj?: DashboardCctSearch) => {
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchDashboardCct({ DashboardCctSearch: sObj });
    },
    [apiFactory]
  );

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { openTabThenSendMessage, onClose } = useTabMessage(identifier);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { dispatchEvent } = useEventsContext();

  const createSuivi = React.useCallback((row: DashboardCctDto) => {
    setRowLoading(row.idapprenant);
    openTabThenSendMessage(`#${ERoutes.apprenant}/${row.idapprenant}/suiviApprenant/0/edit/hope`, {
      ...row,
      typeDernierAvertissement: null
    });
  }, [openTabThenSendMessage]);

  onClose(() => {
    setRowLoading(null);
    dispatchEvent("SEARCH_TABLE_REFRESH");
  })

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: DashboardCctDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => window.open(`#${ERoutes.apprenant}/${row.idapprenant}/suiviApprenant/view`)}
            />
            <ToolTipButton
              tooltipPosition="bottom"
              tooltip={t(ETLCodes.CreateSuivi)}
              minimal
              icon="align-justify"
              onClick={() => createSuivi(row)}
              loading={rowLoading === row.idapprenant}
              disabled={!!rowLoading && rowLoading !== row.idapprenant}
            />
          </ButtonContainer>
        )
      },
      {
        computed: true,
        alignment: "center",
        autoFitContent: true,
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.Statut)}</span>
            <Tooltip
              content={
                <CustomBulletList
                  title={t(ETLCodes.Statut)}
                  items={[
                    {
                      text: t(ETLCodes.EnCours),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.Rompu),
                      color: theme.dangerColor
                    },
                    {
                      text: t(ETLCodes.Suspendu),
                      color: theme.warningColor
                    },
                    {
                      text: t(ETLCodes.JamaisDeContrat),
                      color: Colors.BLACK
                    },
                    {
                      text: t(ETLCodes.Termine),
                      color: Colors.VIOLET4
                    },
                    {
                      text: t(ETLCodes.SansSuite),
                      hideBullet: true
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon
                icon="info-sign"
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          </LegendHeaderContainer>
        ),
        fieldName: "statut",
        render: (row: DashboardCctDto) => (
          <CircleColumn
            color={computeContratStatutColor(row.statutContrat as EStatutContrat, theme)}
            tooltip={tUnsafe(`StatutContrat_${row.statutContrat}`)}
          />
        )
      },
      {
        header: () => t(ETLCodes.Concerne),
        fieldName: "concerne"
      },
      {
        header: () => t(ETLCodes.Age),
        fieldName: "age",
        render: (row: DashboardCctDto) => <>{differenceInYears(new Date(), row.dateNaissance)}</>
      },
      {
        header: () => t(ETLCodes.CodeContrat),
        fieldName: "codeContrat"
      },
      {
        header: () => t(ETLCodes.Metiers),
        fieldName: "metiers"
      },
      {
        header: () => t(ETLCodes.AreCount),
        fieldName: "nbJoursAre"
      },
      {
        header: () => t(ETLCodes.PersonneReferenceIfapme),
        fieldName: "pri"
      },
      {
        header: () => t(ETLCodes.LastCctDate),
        fieldName: "dateDernierCct",
        render: (row: DashboardCctDto) =>
          <ColorText color={row.dateDernierCct && isSameDay(row.dateDernierCct, new Date()) ? theme.sucessColor : "initial"}>{row.dateDernierCct ? format(row.dateDernierCct, "dd-MM-yyyy") : ""}</ColorText>
      },
      {
        header: () => t(ETLCodes.LastCctScreeningDate),
        fieldName: "dateDernierCctScreening",
        render: (row: DashboardCctDto) =>
          <ColorText color={row.dateDernierCctScreening && isSameDay(row.dateDernierCctScreening, new Date()) ? theme.sucessColor : "initial"}>{row.dateDernierCctScreening ? format(row.dateDernierCctScreening, "dd-MM-yyyy") : ""}</ColorText>
      },
      {
        header: () => t(ETLCodes.CentreA),
        fieldName: "centreA"
      },
      {
        header: () => t(ETLCodes.CentreBI),
        fieldName: "centreBI"
      }
    ],
    [createSuivi, rowLoading, t, tUnsafe, theme]
  );

  const getCriteria = React.useCallback(async () => {
    let criterias = await api.dashboardGetCctCriterias();
    criterias.forEach(c => {
      if (c.criteria === "Visibility") {
        c.listValues.forEach(v => (v.displayValue = tUnsafe("ESearchCctType_" + v.displayValue)));
      }
    });
    return criterias;
  }, [api, tUnsafe]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  const customizeRowStyle = React.useCallback(
    (row: DashboardCctDto) => {
      const fontColor = row.nbJoursAre >= 75 && row.nbJoursAre <= 89 ? theme.warningColor : row.nbJoursAre > 89 ? theme.dangerColor : Colors.BLACK;

      return css`
        & td {
          color: ${fontColor} !important;
        }
      `;
    },
    [theme.dangerColor, theme.warningColor]
  );

  return (
    <>
      <SearchTablePage
        searchStateInitialSearch={false}
        withCard={false}
        columns={columns}
        getCriteriasFunction={getCriteria}
        searchFunction={searchFunction}
        onAbort={onAbort}
        customizeRowStyle={customizeRowStyle}
        defaultCriterias={[
          {
            criteria: "Visibility",
            searchMode: EBooleanSearchTypes.Equals,
            value: ESearchCctType.ALL
          }
        ]}
      />
    </>
  );
};
